import { Button, Checkbox, Divider, FormControlLabel, Grid, Stack, Switch, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal, { CommonModalProps } from "..";
import { assetService, setupService } from "../../../Core/Api/services";
import { DbAssetMarkerTypeInfos } from "../../../Core/Api/Types/db-asset-marker-type-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useMultipleSelection } from "../../../Utils/Hooks/useMultipleSelection";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { GetOrderedActionsList, SetIsLoading } from "../../../Redux/Reducers/redux-slice-timetool";
import { UpdateCompilationRequest } from "../../../Core/Api/Types/update-compilation-request ";
import { SequenceMetadataSegment } from "../../../Core/Api/Types/sequence-metadata-segment";
import { TitleManagementMode } from "../../../Core/Api/Types/title-management-mode";
import { useTranslation } from "react-i18next";

export type ModalTimetoolCompilationSkipMarkersElementProps = {
  isGrouped: boolean;
  onGenerated: (segments: SequenceMetadataSegment[]) => void;
};

export type ModalTimetoolCompilationSkipMarkersProps =
  CommonModalProps<ModalTimetoolCompilationSkipMarkersElementProps>;

const ModalTimetoolCompilationSkipMarkers = React.forwardRef<any, ModalTimetoolCompilationSkipMarkersProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { isGrouped, onGenerated, closeModal } = props;
    const dispatch = useDispatch();

    const skipIntroTypeName = "Skip Intro";
    const skipCreditsTypeName = "Skip Credits";

    // utils
    const enqueueSnackbar = useCustomSnackbar();

    // data
    const [checkAllMarkers, setCheckAllMarkers] = useState<boolean>(false);
    const [skipMarkerTypes, setSkipMarkerTypes] = useState<DbAssetMarkerTypeInfos[]>([]);
    const data = useSelector(GetOrderedActionsList);

    // form
    const [keepFirstIntroOfTitles, setKeepFirstIntroOfTitles] = useState<boolean>(false);
    const [keepLastCreditsOfTitles, setKeepLastCreditsOfTitles] = useState<boolean>(false);

    const {
      isSelected,
      select,
      selected: skipMarkersToUseIds,
      selectAll,
      unselectAll,
    } = useMultipleSelection<number>([], skipMarkerTypes?.map((type) => type.id) || []);

    const handleConfirmClick = () => {
      // check assets orders
      let assetIds = data.filter((x) => x.data && x.data.id !== undefined).map((x) => Number(x.data.id));

      let request = new UpdateCompilationRequest({
        assetIds: assetIds,
        skipMarkersToUseIds: skipMarkersToUseIds,
        keepFirstIntroOfTitles: keepFirstIntroOfTitles,
        keepLastCreditsOfTitles: keepLastCreditsOfTitles,
        titleManagementMode: isGrouped ? TitleManagementMode.Grouped : TitleManagementMode.Mixed,
      });

      assetService.UpdateCompilation(request).then((response) => {
        if (!response.success) enqueueSnackbar(response.message || t("alert:error.somethingWentWrong"), "error");
        else {
          dispatch(SetIsLoading(true));
          enqueueSnackbar(t("alert:success.compilationUpdated"), "success");
          onGenerated(response.segments);
          closeModal && closeModal();
        }
      });
    };

    const handleCancelClick = () => {
      closeModal();
    };

    const handleCheckAll = () => {
      if (!checkAllMarkers) {
        selectAll();
        setKeepFirstIntroOfTitles(true);
        setKeepLastCreditsOfTitles(true);
      } else {
        unselectAll();
        setKeepFirstIntroOfTitles(false);
        setKeepLastCreditsOfTitles(false);
      }
      setCheckAllMarkers(!checkAllMarkers);
    };

    useEffect(() => {
      setupService.GetMarkerTypesByFamilyName("Skip").then((response) => {
        if (response.success) {
          setSkipMarkerTypes(response.markerTypeInfos);
        } else enqueueSnackbar(response.message || t("alert:error.fetchingSkipMarkersFamily"), "error");
      });
    }, []);

    return (
      <Modal
        title={t("modal:title.skipMarkers")}
        reference={ref}
        closeModal={handleCancelClick}
        width={800}
        footer={
          <Stack direction="row" justifyContent="space-between">
            <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
            <Button color="success" variant="outlined" onClick={handleConfirmClick}>
              {t("common:confirm")}
            </Button>
          </Stack>
        }
      >
        <Stack spacing={3}>
          <Stack spacing={2}>
            <OutlinedCheckbox checked={checkAllMarkers} onClick={() => handleCheckAll()} sx={{ flex: 1 }}>
              <Typography>{t("modal:body.checkOrUncheckAll")}</Typography>
            </OutlinedCheckbox>

            <Divider />
            {!skipMarkerTypes.length && <Typography>{t("modal:body.noSkipMarkerFound")}</Typography>}
            <Grid container spacing={1}>
              {skipMarkerTypes.map((type) => (
                <React.Fragment key={type.id}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={<Checkbox checked={isSelected(type.id)} onChange={() => select(type.id)} />}
                      label={type.name}
                      //sx={{ pl: 2.5 }}
                    />
                  </Grid>
                  {type.name === skipIntroTypeName && (
                    <Grid item xs={6}>
                      <FormControlLabel
                        checked={keepFirstIntroOfTitles}
                        value={keepFirstIntroOfTitles}
                        onChange={(e, value) => {
                          if (!isSelected(type.id) && value) select(type.id);

                          setKeepFirstIntroOfTitles(value);
                        }}
                        control={<Switch />}
                        label={t("field:label.keepFirstIntroOfTitle")}
                        disabled={!isGrouped}
                      />
                    </Grid>
                  )}
                  {type.name === skipCreditsTypeName && (
                    <Grid item xs={6}>
                      <FormControlLabel
                        checked={keepLastCreditsOfTitles}
                        value={keepLastCreditsOfTitles}
                        onChange={(e, value) => {
                          if (!isSelected(type.id) && value) select(type.id);
                          setKeepLastCreditsOfTitles(value);
                        }}
                        control={<Switch />}
                        label={t("field:label.keepLastCreditsOfTitle")}
                        disabled={!isGrouped}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>
          </Stack>
        </Stack>
      </Modal>
    );
  }
);

export default ModalTimetoolCompilationSkipMarkers;
